// Configuration management utility
// Uses new environment validation system
import { validateEnvironment, displayEnvironmentInfo, type EnvironmentConfig } from './environment';

// Validate environment on module load
const environment: EnvironmentConfig = validateEnvironment();

// Display environment info in development
if (environment.isDevelopment) {
  displayEnvironmentInfo();
}

class ConfigurationManager {
  private environment: EnvironmentConfig;
  private dynamicAppRegistrations: {
    teamsPresenceClientId?: string;
    contactSyncClientId?: string;
  } | null = null;
  
  constructor() {
    this.environment = environment;
  }
  
  // Fetch dynamic app registrations from API
  async fetchAppRegistrations(): Promise<void> {
    try {
      const response = await fetch(`${this.environment.apiBaseUrl}/api/app-config/app-registrations`);
      if (response.ok) {
        this.dynamicAppRegistrations = await response.json();
        console.log('✅ Fetched app registrations from API:', this.dynamicAppRegistrations);
      }
    } catch (error) {
      console.warn('⚠️ Failed to fetch app registrations from API, using environment defaults:', error);
    }
  }
  
  // Getters for configuration values
  get azure() {
    return {
      clientId: this.environment.azureClientId,
      tenantId: this.environment.azureTenantId,
      authority: this.environment.azureAuthority,
      apiAppIdUri: this.environment.apiAppIdUri,
    };
  }
  
  get api() {
    return {
      baseUrl: this.environment.apiBaseUrl,
    };
  }
  
  get azureMaps() {
    return {
      key: this.environment.azureMapsKey,
    };
  }
  
  get development() {
    return {
      logLevel: this.environment.logLevel,
    };
  }
  
  get features() {
    return {
      analytics: this.environment.enableAnalytics,
      monitoring: this.environment.enableMonitoring,
      presence: this.environment.enablePresence,
    };
  }
  
  get appRegistrations() {
    return {
      teamsPresenceClientId: this.dynamicAppRegistrations?.teamsPresenceClientId || this.environment.teamsPresenceClientId,
      contactSyncClientId: this.dynamicAppRegistrations?.contactSyncClientId,
    };
  }
  
  // Method to check if we're in development mode
  get isDevelopment(): boolean {
    return this.environment.isDevelopment;
  }
  
  get isProduction(): boolean {
    return this.environment.isProduction;
  }
  
  // Method to get full configuration (for debugging)
  getFullConfig(): EnvironmentConfig {
    return { ...this.environment };
  }
}

// Create and export singleton instance
export const config = new ConfigurationManager();

// Export types for use in other files
export type { EnvironmentConfig as AppConfig };
export { ConfigurationManager };
